/**
 * The 44px column at the end of a contract row.
 *
 * Only entries that do something. Downloading the PDF is here rather than only
 * on the contract page because that is the one thing an operator wants from a
 * row without reading the record - it is the thing they email onwards.
 *
 * No permission check: `/contracts` already answers to `manage:crm.projects` in
 * `crm/nav.ts`, so anybody who can reach this row can also write to it.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Copy, FileDown, Loader2, MoreHorizontal, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { downloadContractPdf, type Contract } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";

export function ContractRowMenu({ contract, onDelete }: { contract: Contract; onDelete: () => void }) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const [busy, setBusy] = useState(false);

  async function copyNumber() {
    close();
    if (!contract.number) return;
    try {
      await navigator.clipboard.writeText(contract.number);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  async function pdf() {
    close();
    setBusy(true);
    try {
      await downloadContractPdf(contract.id, `${contract.number ?? contract.title}.pdf`);
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <MoreHorizontal className="size-4" />}
          </button>
        )}
      >
        {/* A real Link rather than a button that navigates: the row's own
            stretched overlay is not reachable from inside the menu, and this
            keeps middle-click and "open in new tab" working. */}
        <Link className="crm-mi" to={`/contracts/${contract.id}`} onClick={close}>
          <ArrowUpRight className="size-4" />
          {t("contracts.open")}
        </Link>

        <button type="button" className="crm-mi" onClick={() => void pdf()}>
          <FileDown className="size-4" />
          {t("contracts.downloadPdf")}
        </button>

        {contract.number ? (
          <button type="button" className="crm-mi" onClick={() => void copyNumber()}>
            <Copy className="size-4" />
            {t("contracts.copyNumber")}
          </button>
        ) : null}

        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>
    </span>
  );
}
